import React from "react";
import { CheckCircle2, XCircle, Flag, LayoutGrid, Table } from "lucide-react";
import { CourseConfig } from "../../models/teeBox";
import { PlayerScore } from "../../models/leaderboard";

interface DetailedScorecardProps {
  player: any;
  courseConfig: CourseConfig;
}

interface ScoreRowData {
  label: string;
  values: (string | number | JSX.Element)[];
  className?: string;
}

export function DetailedScorecard({
  player,
  courseConfig,
}: DetailedScorecardProps) {
  const [viewMode, setViewMode] = React.useState<"vertical" | "horizontal">(
    "vertical"
  );

  const getTeeConfig = () => {
    if (player.gender === "F") return courseConfig.tees.ladies;
    if (player.ageCategory === "senior") return courseConfig.tees.seniors;
    return courseConfig.tees.men;
  };

  const getYardage = (holeNumber: number) => {
    const hole = courseConfig.holes.find((h) => h.holeNumber === holeNumber);
    if (!hole) return null;

    if (player.gender === "F") return hole.yardages.ladies.yardage;
    if (player.ageCategory === "senior") return hole.yardages.seniors.yardage;
    return hole.yardages.men.yardage;
  };

  const teeConfig = getTeeConfig();
  const totalYardage = courseConfig.holes.reduce((total, hole) => {
    const yardage =
      player.gender === "F"
        ? hole.yardages.ladies.yardage
        : player.ageCategory === "senior"
        ? hole.yardages.seniors.yardage
        : hole.yardages.men.yardage;
    return total + yardage;
  }, 0);

  const totalPar = courseConfig.holes.reduce(
    (total, hole) => total + hole.par,
    0
  );

  const renderHorizontalScorecard = () => {
    const frontNine = player.playerScores.slice(0, 9);
    const backNine = player.playerScores.slice(9, 18);

    const createScoreRow = (scores: PlayerScore[]): ScoreRowData[] => {
      const holes = scores.map((s) => s.holeNumber);
      const pars = scores.map((s) => {
        const hole = courseConfig.holes.find(
          (h) => h.holeNumber === s.holeNumber
        );
        return hole?.par || "-";
      });
      const yards = scores.map((s) => {
        // const hole = courseConfig.holes.find(h => h.holeNumber === s.holeNumber);
        return getYardage(s.holeNumber) || "-";
      });
      const si = scores.map((s) => {
        const hole = courseConfig.holes.find(
          (h) => h.holeNumber === s.holeNumber
        );
        return hole?.strokeIndex || "-";
      });
      const playerScores = scores.map((s) => (
        <div className="flex items-center justify-center">
          <span
            className={
              s.score <
              (courseConfig.holes.find((h) => h.holeNumber === s.holeNumber)
                ?.par || 0)
                ? "text-green-600"
                : s.score >
                  (courseConfig.holes.find((h) => h.holeNumber === s.holeNumber)
                    ?.par || 0)
                ? "text-red-600"
                : "text-gray-900 dark:text-white"
            }
          >
            {s.score}
          </span>
          {s.isValid ? (
            <CheckCircle2 className="w-3 h-3 text-green-500 ml-1" />
          ) : (
            <XCircle className="w-3 h-3 text-red-500 ml-1" />
          )}
        </div>
      ));
      const points = scores.map((s) => s.stablefordScore);

      return [
        { label: "Hole", values: holes, className: "font-medium bg-gray-50" },
        { label: "Par", values: pars },
        { label: "Yards", values: yards },
        { label: "SI", values: si },
        { label: "Score", values: playerScores },
        { label: "Points", values: points },
      ];
    };

    return (
      <div className="space-y-8">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-2 py-2 bg-gray-50 dark:bg-gray-900 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20">
                  Front 9
                </th>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                  <th
                    key={num}
                    className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-12"
                  >
                    {num}
                  </th>
                ))}
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16">
                  Out
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {createScoreRow(frontNine).map((row) => (
                <tr
                  key={row.label}
                  className={`${row.className} dark:bg-gray-800`}
                >
                  <td className="px-2 py-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {row.label}
                  </td>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((_, i) => {
                    const value = row.values[i];
                    return (
                      <td
                        key={i}
                        className="px-2 py-2 text-center text-sm text-gray-900 dark:text-white"
                      >
                        {value}
                      </td>
                    );
                  })}
                  <td className="px-2 py-2 text-center text-sm font-medium text-gray-900 dark:text-white">
                    {row.label === "Score" &&
                      frontNine.reduce(
                        (sum: number, s: PlayerScore) => sum + s.score,
                        0
                      )}
                    {row.label === "Points" &&
                      frontNine.reduce(
                        (sum: number, s: PlayerScore) =>
                          sum + s.stablefordScore,
                        0
                      )}
                    {row.label === "Par" &&
                      frontNine.reduce((sum: number, s: PlayerScore) => {
                        const hole = courseConfig.holes.find(
                          (h) => h.holeNumber === s.holeNumber
                        );
                        return sum + (hole?.par || 0);
                      }, 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-2 py-2 bg-gray-50 dark:bg-gray-900 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20">
                  Back 9
                </th>
                {[10, 11, 12, 13, 14, 15, 16, 17, 18].map((num) => (
                  <th
                    key={num}
                    className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-12"
                  >
                    {num}
                  </th>
                ))}
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16 border-l border-gray-200 dark:border-gray-700">
                  In
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16 border-l border-gray-200 dark:border-gray-700">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {createScoreRow(backNine).map((row) => (
                <tr
                  key={row.label}
                  className={`${row.className} dark:bg-gray-800`}
                >
                  <td className="px-2 py-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {row.label}
                  </td>
                  {[10, 11, 12, 13, 14, 15, 16, 17, 18].map((_, i) => {
                    const value = row.values[i];
                    return (
                      <td
                        key={i}
                        className="px-2 py-2 text-center text-sm text-gray-900 dark:text-white"
                      >
                        {value}
                      </td>
                    );
                  })}
                  {/* Back 9 Total */}
                  <td className="px-2 py-2 text-center text-sm font-medium border-l border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white">
                    {row.label === "Score"
                      ? backNine.reduce(
                          (sum: number, s: PlayerScore) => sum + s.score,
                          0
                        )
                      : row.label === "Points"
                      ? backNine.reduce(
                          (sum: number, s: PlayerScore) =>
                            sum + s.stablefordScore,
                          0
                        )
                      : row.label === "Par"
                      ? backNine.reduce((sum: number, s: PlayerScore) => {
                          const hole = courseConfig.holes.find(
                            (h) => h.holeNumber === s.holeNumber
                          );
                          return sum + (hole?.par || 0);
                        }, 0)
                      : row.label === "Yards"
                      ? backNine.reduce(
                          (sum: number, s: PlayerScore) =>
                            sum + (getYardage(s.holeNumber) || 0),
                          0
                        )
                      : "-"}
                  </td>
                  {/* Total for all 18 holes */}
                  <td className="px-2 py-2 text-center text-sm font-medium border-l border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white">
                    {row.label === "Score"
                      ? player.totalGross
                      : row.label === "Points"
                      ? player.totalStableford
                      : row.label === "Par"
                      ? totalPar
                      : row.label === "Yards"
                      ? totalYardage
                      : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 transition-colors duration-200">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-4">
          {player.profilePicture && (
            <img
              src={`/api/static/images/${player.profilePicture}`}
              alt={`${player.firstName} ${player.lastName}`}
              className="w-16 h-16 rounded-full"
            />
          )}
          <div className="min-w-0">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {player.firstName} {player.lastName}
            </h2>
            <div className="flex items-center mt-1 space-x-4">
              <span className="text-gray-600 dark:text-gray-400">
                {player.category}
              </span>
              <span className="text-gray-600 dark:text-gray-400">
                HCP: {player.handicapRounded}
              </span>
              <span className="text-gray-600 dark:text-gray-400">
                Gross: {player.totalGross}
              </span>
              <span className="text-gray-600 dark:text-gray-400">
                Stbl: {player.totalStableford}
              </span>
            </div>
          </div>
        </div>
        <div className="flex-shrink-0 w-full sm:w-auto mt-4 sm:mt-0 sm:ml-auto">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {courseConfig.courseName}
          </h3>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center">
              Tee: <span className="w-4 h-4 rounded-full ml-2 tee-color"></span>
            </div>
            <div>Slope: {teeConfig.slopeIndex}</div>
            <div>Rating: {teeConfig.courseRating}</div>
          </div>
        </div>
      </div>

      <div className="flex justify-end mb-4">
        <button
          onClick={() =>
            setViewMode(viewMode === "vertical" ? "horizontal" : "vertical")
          }
          className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
        >
          {viewMode === "vertical" ? (
            <>
              <Table className="h-4 w-4" />
              <span className="hidden">Show Horizontal</span>
            </>
          ) : (
            <>
              <LayoutGrid className="h-4 w-4" />
              <span className="hidden">Show Vertical</span>
            </>
          )}
        </button>
      </div>

      <div className="overflow-x-auto">
        {viewMode === "horizontal" ? (
          renderHorizontalScorecard()
        ) : (
          <>
            <div className="md:hidden">
              {player.playerScores.map((score: any) => {
                const hole = courseConfig.holes.find(
                  (h) => h.holeNumber === score.holeNumber
                );
                if (!hole) return null;

                const yardage =
                  player.category === "L"
                    ? hole.yardages.ladies.yardage
                    : player.category === "S"
                    ? hole.yardages.seniors.yardage
                    : hole.yardages.men.yardage;

                return (
                  <div
                    key={score.holeNumber}
                    className="py-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center">
                        <Flag className="w-4 h-4 text-green-600 mr-2" />
                        <span className="font-medium text-gray-900 dark:text-white">
                          Hole {score.holeNumber}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        SI: {hole.strokeIndex}
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-sm">
                      <div>
                        <div className="text-gray-500 dark:text-gray-400">
                          Par
                        </div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {hole.par}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 dark:text-gray-400">
                          Yards
                        </div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {yardage}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 dark:text-gray-400">
                          Gross
                        </div>
                        <div className="flex items-center font-medium text-gray-900 dark:text-white">
                          <span
                            className={`${
                              score.score < hole.par
                                ? "text-green-600"
                                : score.score > hole.par
                                ? "text-red-600"
                                : "text-gray-900 dark:text-white"
                            }`}
                          >
                            {score.score}
                          </span>
                          {score.isValid ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500 ml-1" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-500 ml-1" />
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 dark:text-gray-400">
                          Points
                        </div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {score.stablefordScore}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop Table View */}
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 hidden md:table">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <th className="px-3 py-3 hidden md:table-cell">Hole</th>
                  <th className="px-3 py-3 hidden md:table-cell">Par</th>
                  <th className="px-3 py-3 hidden md:table-cell">SI</th>
                  <th className="px-3 py-3 hidden md:table-cell">Yards</th>
                  <th className="px-3 py-3">Score</th>
                  <th className="px-3 py-3">Points</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {player.playerScores.map((score: any) => {
                  const holeConfig = courseConfig.holes.find(
                    (h) => h.holeNumber === score.holeNumber
                  );
                  if (!holeConfig) return null;

                  return (
                    <tr
                      key={score.holeNumber}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 text-sm transition-colors duration-200"
                    >
                      <td className="px-3 py-3 whitespace-nowrap font-medium text-gray-900 dark:text-white hidden md:table-cell">
                        {score.holeNumber}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-gray-900 dark:text-white hidden md:table-cell">
                        {holeConfig.par}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-gray-900 dark:text-white hidden md:table-cell">
                        {holeConfig.strokeIndex}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-gray-900 dark:text-white hidden md:table-cell">
                        {getYardage(score.holeNumber)}
                      </td>
                      <td
                        className={`px-3 py-3 whitespace-nowrap font-medium flex items-center ${
                          score.score < holeConfig.par
                            ? "text-green-600"
                            : score.score > holeConfig.par
                            ? "text-red-600"
                            : "text-gray-900 dark:text-white"
                        }`}
                      >
                        <span className="md:hidden mr-2">
                          H{score.holeNumber}:
                        </span>
                        <span>{score.score}</span>
                        {score.isValid ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500 ml-2" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500 ml-2" />
                        )}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {score.stablefordScore}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    Total
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white hidden md:table-cell">
                    {totalPar}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white hidden md:table-cell">
                    -
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white hidden md:table-cell">
                    {totalYardage}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    <span className="md:hidden">Total: </span>
                    {player.totalGross}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {player.totalStableford}
                  </td>
                </tr>
              </tfoot>
            </table>
          </>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg md:rounded-lg transition-colors duration-200">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">
            HCP Adj
          </h3>
          <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
            {player.handicapAdjustment}
          </p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg md:rounded-lg transition-colors duration-200">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">
            Rev CH
          </h3>
          <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
            {player.revisedCourseHandicap}
          </p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg md:rounded-lg transition-colors duration-200">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">
            Rank
          </h3>
          <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
            {player.rankingScore && player.rankingScore.toFixed(2)}
          </p>
        </div>
      </div>
    </div>
  );
}
